import React, { useEffect, useState } from 'react';
import { Container, Table, Pagination, Alert, Card } from 'react-bootstrap';
import { collection, query, orderBy, limit, getDocs, startAfter, where } from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import { useNavigate } from 'react-router-dom';

interface LoginLog {
  email: string;
  timestamp: string;
  success: boolean;
  id?: string;
}

const ITEMS_PER_PAGE = 25;

export default function LoginLedger() {
  const [logs, setLogs] = useState<LoginLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [totalItems, setTotalItems] = useState<number>(0);
  const navigate = useNavigate();

  useEffect(() => {
    const checkSuperUser = async () => {
      try {
        if (auth.currentUser?.email) {
          console.log('Checking super user status for:', auth.currentUser.email);
          const superUsersRef = collection(db, 'super_users');
          const q = query(superUsersRef, where('email', '==', auth.currentUser.email));
          const querySnapshot = await getDocs(q);
          
          console.log('Super user query results:', querySnapshot.size);
          querySnapshot.forEach(doc => {
            console.log('Super user doc data:', doc.data());
          });
          
          const isSuper = !querySnapshot.empty;
          console.log('Setting isSuperUser to:', isSuper);
          setIsSuperUser(isSuper);
          
          if (!isSuper) {
            console.log('User is not a super user, redirecting to dashboard');
            navigate('/dashboard');
          }
        } else {
          console.log('No authenticated user, redirecting to login');
          navigate('/login');
        }
      } catch (error) {
        console.error('Error checking super user status:', error);
        navigate('/dashboard');
      }
    };

    checkSuperUser();
  }, [navigate]);

  const fetchLogsForPage = async (page: number) => {
    if (!isSuperUser) return;
    
    try {
      setLoading(true);
      setError(null);

      const logsRef = collection(db, 'loginLogs');
      
      // Get total count if we're on the first page
      if (page === 1) {
        const countSnapshot = await getDocs(collection(db, 'loginLogs'));
        setTotalItems(countSnapshot.size);
      }

      let q = query(
        logsRef,
        orderBy('timestamp', 'desc'),
        limit(ITEMS_PER_PAGE)
      );

      // Add startAfter if not on first page
      if (page > 1) {
        const offset = (page - 1) * ITEMS_PER_PAGE;
        const snapshot = await getDocs(query(
          logsRef,
          orderBy('timestamp', 'desc'),
          limit(offset)
        ));
        const lastVisible = snapshot.docs[snapshot.docs.length - 1];
        q = query(
          logsRef,
          orderBy('timestamp', 'desc'),
          startAfter(lastVisible),
          limit(ITEMS_PER_PAGE)
        );
      }

      console.log('Executing login logs query for page:', page);
      const querySnapshot = await getDocs(q);
      console.log('Received login logs:', querySnapshot.size);
      
      const logData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as LoginLog[];

      setLogs(logData);
      setHasMore(logData.length === ITEMS_PER_PAGE && (page * ITEMS_PER_PAGE) < totalItems);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching login logs:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Error loading login logs: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperUser) {
      fetchLogsForPage(1);
    }
  }, [isSuperUser]);

  const getItemRangeText = () => {
    const start = ((currentPage - 1) * ITEMS_PER_PAGE) + 1;
    const end = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);
    return `Showing ${start}-${end} out of ${totalItems} total items`;
  };

  if (!isSuperUser) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          You do not have permission to view this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container style={{ maxWidth: '1000px', padding: '20px' }}>
      <h2 className="mb-4">Login Ledger</h2>
      
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Email</th>
                <th>Timestamp</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td>{log.email}</td>
                  <td>{new Date(log.timestamp).toLocaleString()}</td>
                  <td>
                    {log.success ? (
                      <span className="text-success">
                        <i className="bi bi-check-circle me-1"></i>
                        Success
                      </span>
                    ) : (
                      <span className="text-danger">
                        <i className="bi bi-x-circle me-1"></i>
                        Failed
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {loading && <div className="text-center my-3">Loading...</div>}

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted">
              {getItemRangeText()}
            </div>
            {totalItems > ITEMS_PER_PAGE && (
              <Pagination>
                <Pagination.Prev
                  onClick={() => fetchLogsForPage(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                />
                <Pagination.Item active>{currentPage}</Pagination.Item>
                <Pagination.Next
                  onClick={() => fetchLogsForPage(currentPage + 1)}
                  disabled={!hasMore || loading}
                />
              </Pagination>
            )}
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
} 